type TBatchFetch<TValue> = (ids: string[]) => Promise<Record<string, TValue>>

type TWaiter<TValue> = {
    resolve: (value: TValue | undefined) => void
    reject: (error: unknown) => void
}

const MAX_BATCH = 50

/**
 * The `follow/*` endpoints take a list of usernames, but every card asks about its own author.
 * Callers keep asking one at a time; requests raised in the same tick go out as a single batch.
 */
export const createUsernameBatcher = <TValue>(fetchBatch: TBatchFetch<TValue>) => {
    let queue = new Map<string, TWaiter<TValue>[]>()
    let scheduled = false

    const send = (batch: Map<string, TWaiter<TValue>[]>) => {
        const usernames = [...batch.keys()]

        fetchBatch(usernames.map(username => `_${username}`))
            .then(result => {
                batch.forEach((waiters, username) => {
                    waiters.forEach(waiter => waiter.resolve(result[username]))
                })
            })
            .catch(error => {
                batch.forEach(waiters => {
                    waiters.forEach(waiter => waiter.reject(error))
                })
            })
    }

    const flush = () => {
        scheduled = false
        const pending = queue
        queue = new Map()

        const entries = [...pending.entries()]

        for (let index = 0; index < entries.length; index += MAX_BATCH) {
            send(new Map(entries.slice(index, index + MAX_BATCH)))
        }
    }

    return (username: string): Promise<TValue | undefined> =>
        new Promise((resolve, reject) => {
            const waiters = queue.get(username) ?? []
            waiters.push({ resolve, reject })
            queue.set(username, waiters)

            if (!scheduled) {
                scheduled = true
                setTimeout(flush, 0)
            }
        })
}
